import { useEffect, useMemo, useRef } from "react";
import { PRELOAD_MESSAGES_PER_CHANNEL } from "@ano/shared/queries";
import { useChannelMessages } from "@/lib/selectors/composite-channel";
import {
  shouldShowMessageLoadingSkeleton,
  shouldUseMessageReconnectSnapshot,
} from "@/lib/message-feed-state";
import { markVisible } from "@/lib/send-trace";
import { isChannelTrusted } from "@/lib/channel-navigation-trust";
import { POPIN_DEBUG } from "@/lib/debug-flags";
import { csMark } from "@/lib/channel-switch-profile";
import { useStableSet } from "@/lib/use-stable-set";
import {
  filterDeferredThreadCreatedRows,
  deriveThreadSourceIds,
} from "./thread-system-row-gate";

/** A single materialized row from `useChannelMessages`. */
type MessageListItem = NonNullable<
  ReturnType<typeof useChannelMessages>[0]
>[number];

interface UseChannelMessageListArgs {
  channelId: string | null;
  messageLimit: number;
  channelCreatedAt: number | null;
  isDmChannel: boolean;
}

interface UseChannelMessageListResult {
  /** Newest-first slice from Zero (`channelMessages[0]` is the newest). */
  channelMessages: readonly MessageListItem[];
  /** Oldest-first slice rendered by `MessageListView`. */
  messageList: readonly MessageListItem[];
  /** `id -> message` map over `messageList`. */
  messageById: Map<string, MessageListItem>;
  /**
   * Source-message IDs that have a `kind="system"` thread-created
   * announcement in the channel — i.e. the user clicked "Create Thread"
   * on them. Drives the "Thread • N messages" badge + side-bar accent
   * on the source row, and gates whether the system announcement row
   * itself is visible (filtered out until `source.reply_count > 0`).
   * Stabilized via `useStableSet` so the reference only changes when
   * membership actually changes.
   */
  threadSourceIds: ReadonlySet<string>;
  /** `created_at` of the newest message in the rendered slice (`0` when empty). */
  latestMessageCreatedAt: number;
  /** True iff the rendered slice came from the reconnect snapshot. */
  isUsingReconnectSnapshot: boolean;
  /** True iff the loading skeleton should mount (cold mount, no local data). */
  showMessageLoadingSkeleton: boolean;
  /** Sliced query status, normalized for the `MessageListView` prop. */
  channelMessagesStatusForList:
    | { type: "unknown" | "complete" }
    | { type: "error"; retry?: () => void };
  /**
   * `true` when there might be more older messages to load. Drives the
   * "load older" pill + the bump path.
   */
  hasMoreMessages: boolean;
}

/**
 * Owns the per-channel message slice + reconnect snapshot + skeleton
 * state for `MessageArea`.
 *
 * The slice is sourced from `useChannelMessages` (a thin `useQuery`
 * wrapper over the shared `byChannelComposite` materialization). On
 * a websocket reconnect, when Zero briefly reports `unknown` and an
 * empty array even though a slice was recently visible, we keep
 * showing the previous snapshot so the user doesn't see a flash of
 * empty state.
 *
 * Reconnect snapshot is keyed by `channelId` so multiplexed channel
 * switches don't clobber each other's last-seen content.
 */
export function useChannelMessageList({
  channelId,
  messageLimit,
  channelCreatedAt,
  isDmChannel,
}: UseChannelMessageListArgs): UseChannelMessageListResult {
  // Channel message list — render-isolated subscription on top of the
  // shared `byChannelComposite` materialization. `useChannelMessages`
  // projects the composite rows down to a stripped-of-children
  // `ChannelMessage[]` and only fires a React update when something
  // material to the message list changes. Reactions / attachments /
  // link-preview updates DO NOT cause MessageArea to re-render — those
  // arrive via dedicated child-Map subscriptions inside MessageListView,
  // which all share the same materialized view via the ViewCache.
  //
  // This preserves the render-isolation contract that the per-table
  // selector design (Phase 5.1, commit `e2365bec`) was built to enforce,
  // on top of the single-subscription composite shape.
  const [channelMessagesDesc, channelMessagesStatus] = useChannelMessages(
    channelId,
    messageLimit,
  );
  const channelMessages = useMemo(
    () => channelMessagesDesc ?? [],
    [channelMessagesDesc],
  );
  const reconnectSnapshotRef = useRef<Record<string, MessageListItem[]>>({});

  // Channel-switch profile: mark `emit` the first time we see messages
  // for a new channelId. Independent of POPIN_DEBUG so the cs profile
  // can run alone. No-op when the flag is off (single ref read).
  const csEmitMarkedRef = useRef<string | null>(null);
  if (channelId && csEmitMarkedRef.current !== channelId) {
    csEmitMarkedRef.current = channelId;
    csMark("emit", channelId, { msgs: channelMessages.length });
  }

  // Pop-in diagnostic — see POPIN_DEBUG in debug-flags.ts. One line per
  // channel emit: shows first paint's count + every subsequent
  // delta until quiescence. Cheap when flag is off (single ref read).
  const popinPrevRef = useRef<{
    channelId: string | null;
    ids: string[];
    t0: number;
    emit: number;
  } | null>(null);
  if (POPIN_DEBUG && channelId) {
    const ids = channelMessages.map((m) => m.id);
    const prev = popinPrevRef.current;
    if (!prev || prev.channelId !== channelId) {
      // First emit for this channel
      const t0 =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      console.debug(
        `[popin] INITIAL ch=${channelId.slice(-6)} count=${ids.length} ` +
          `status=${channelMessagesStatus.type} ` +
          `headId=${ids[0]?.slice(-6) ?? "-"} ` +
          `tailId=${ids[ids.length - 1]?.slice(-6) ?? "-"}`,
      );
      popinPrevRef.current = { channelId, ids, t0, emit: 1 };
    } else {
      const prevSet = new Set(prev.ids);
      const nextSet = new Set(ids);
      const added = ids.filter((id) => !prevSet.has(id));
      const removed = prev.ids.filter((id) => !nextSet.has(id));
      if (added.length > 0 || removed.length > 0) {
        const t1 =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const dt = Math.round(t1 - prev.t0);
        // Where did the additions land? Head = newer, tail = older.
        // Position 0 = newest (after .reverse() this becomes the
        // *last* visual row in the rendered list — bottom of channel).
        // Position length-1 = oldest top of channel.
        const positions = added.map((id) => ids.indexOf(id));
        const minPos = positions.length ? Math.min(...positions) : -1;
        const maxPos = positions.length ? Math.max(...positions) : -1;
        const where =
          added.length === 0
            ? "-"
            : maxPos === ids.length - 1 && minPos === ids.length - added.length
              ? "TAIL (oldest end)"
              : minPos === 0 && maxPos === added.length - 1
                ? "HEAD (newest end)"
                : `MIXED (positions ${minPos}..${maxPos} of ${ids.length - 1})`;
        console.debug(
          `[popin] DELTA ch=${channelId.slice(-6)} emit=${prev.emit + 1} ` +
            `+${dt}ms count=${prev.ids.length}→${ids.length} ` +
            `added=${added.length} removed=${removed.length} ` +
            `where=${where} ` +
            `addedIds=[${added
              .slice(0, 5)
              .map((id) => id.slice(-6))
              .join(",")}${added.length > 5 ? `,…+${added.length - 5}` : ""}]`,
        );
        popinPrevRef.current = {
          channelId,
          ids,
          t0: prev.t0,
          emit: prev.emit + 1,
        };
      }
    }
  }

  // Phase 7c-D1b: derive `mentions` from the `message_mentions` junction
  // (hydrated by `byChannelComposite`'s `.related("message_mentions")`
  // chain). The legacy `messages.mentions` JSONB column is still
  // populated by writers — but downstream consumers
  // (`use-send-to-shell.ts` fast-path, `MessageDataStore.entryEqual`)
  // now see junction-derived data, completing the desktop reader flip.
  // Once 7c-D drops the JSONB column, only the junction remains.
  //
  // The derived array is `.sort()`-ed because `MessageDataStore.entryEqual`
  // does an order-sensitive `stringArrayEqual` on `prev.mentions` /
  // `next.mentions`, and Zero's `.related()` over a junction has no
  // documented row-order guarantee (and `.orderBy` is unsupported on
  // junction relations — see Zero docs). Without sorting, two IVM
  // emissions of the same logical mention set in different order would
  // false-diff and force a `MessageRow` re-render, defeating the
  // per-row memoization this hook is built around.
  const baseMessageListRaw = useMemo(
    () =>
      [...channelMessages].reverse().map((m) => ({
        ...m,
        mentions: Array.isArray(
          (m as { message_mentions?: ReadonlyArray<{ user_id: string }> })
            .message_mentions,
        )
          ? (
              m as { message_mentions: ReadonlyArray<{ user_id: string }> }
            ).message_mentions
              .map((mm) => mm.user_id)
              .sort()
          : null,
      })),
    [channelMessages],
  );

  // Discord-shape: hide the "X started a thread" system announcement
  // until the thread actually has activity. The pure helper is in
  // `thread-system-row-gate.ts` — see that file for the filter rules.
  // The system row stays in the DB; this is a render filter only.
  const baseMessageList = useMemo(
    () => filterDeferredThreadCreatedRows(baseMessageListRaw),
    [baseMessageListRaw],
  );

  // Set of source-message IDs whose thread_created announcement exists
  // in the slice. Built from the UNFILTERED list so a hidden
  // announcement (no replies yet) still keeps `threadSourceIds.has(id)`
  // false — gating on this set means the badge appears only when the
  // announcement is visible.
  const threadSourceIdsRaw = useMemo(
    () => deriveThreadSourceIds(baseMessageListRaw),
    [baseMessageListRaw],
  );
  const threadSourceIds = useStableSet(threadSourceIdsRaw);

  const reconnectSnapshotForChannel = channelId
    ? (reconnectSnapshotRef.current[channelId] ?? [])
    : [];

  const isUsingReconnectSnapshot = shouldUseMessageReconnectSnapshot({
    channelId,
    queryState: channelMessagesStatus.type,
    liveMessageCount: baseMessageList.length,
    snapshotCount: reconnectSnapshotForChannel.length,
  });

  const messageList = isUsingReconnectSnapshot
    ? reconnectSnapshotForChannel
    : baseMessageList;

  const latestMessageCreatedAt =
    messageList.length > 0 ? messageList[messageList.length - 1].created_at : 0;
  // Track "has this instance ever seen the channel query complete"
  // — distinguishes cold mount (skeleton) from WebSocket reconnect
  // (keep showing current/snapshot content). The workspace preload
  // has a different query identity from this channel-rooted active
  // query, so the active query opens its own server pipeline; Zero's
  // prefix guarantee (matching sort order) means whatever the
  // preload populated locally is a valid prefix of the active
  // result and renders without re-shuffling.
  const hasCompletedOnceRef = useRef(false);
  if (channelMessagesStatus.type === "complete") {
    hasCompletedOnceRef.current = true;
  }
  // Skip skeleton on freshly-created OR just-opened channels that have
  // no local messages. Two OR'd signals:
  //   • created_at < 60s — a brand-new channel. Requires the composite
  //     (byIdComposite → channelCreatedAt) to have hydrated.
  //   • isChannelTrusted — an explicit open/create flow just confirmed
  //     this channel (NewDMDialog + useOpenDM call trustChannel before
  //     navigating). This fires BEFORE byIdComposite hydrates, so the
  //     "+ → new DM with someone never messaged" path no longer hangs on
  //     the loading skeleton while the composite (the source of
  //     channelCreatedAt) is still in flight. Sidebar-open already worked
  //     because the composite was cached; this brings the create path to
  //     parity.
  const channelKnownEmpty =
    messageList.length === 0 &&
    ((channelCreatedAt !== null && Date.now() - channelCreatedAt < 60_000) ||
      (channelId !== null && isChannelTrusted(channelId)));

  // Threshold matches the workspace preload's per-channel limit
  // (`PRELOAD_MESSAGES_PER_CHANNEL`), NOT the active query's
  // `MESSAGE_PAGE_SIZE`. The preload populates local SQLite with
  // exactly `PRELOAD_MESSAGES_PER_CHANNEL` contiguous most-recent
  // messages per channel; once `messageCount` reaches that bar the
  // skeleton lifts and the active query's deeper pages (51-300 +
  // rich joins) fade in within ~100-200ms. Using `MESSAGE_PAGE_SIZE`
  // here would hide the preloaded rows behind the skeleton until the
  // active server query delivered the full page — the "instant feel"
  // the preload was supposed to buy us.
  //
  // Safety against scatter from the inbox (`useQuery` 5-min TTL on
  // `messages.inboxForUser()`): inbox events for a single channel
  // are sparse — typically far fewer than the threshold — so scatter
  // alone almost never reaches the gate, and the skeleton continues
  // to mask it. Inbox-restructure work to drop this risk entirely is
  // tracked as a follow-up.
  const showMessageLoadingSkeleton = shouldShowMessageLoadingSkeleton({
    channelId,
    queryState: channelMessagesStatus.type,
    messageCount: messageList.length,
    expectedMinCount: PRELOAD_MESSAGES_PER_CHANNEL,
    hasCompletedOnce: hasCompletedOnceRef.current,
    channelKnownEmpty,
    allowPartialLocalData: isDmChannel,
  });

  // Debug: log skeleton-state transitions per channel. Toggle via
  //   localStorage.setItem('ano-debug-preload', '1')
  // No-op when the flag is absent. Per-channel ref keyed memory so
  // we only emit on actual transitions, not every render.
  const prevSkeletonRef = useRef<{ id: string | null; show: boolean | null }>({
    id: null,
    show: null,
  });
  if (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("ano-debug-preload") === "1" &&
    (prevSkeletonRef.current.id !== channelId ||
      prevSkeletonRef.current.show !== showMessageLoadingSkeleton)
  ) {
    console.log(
      `[skel] channel=${channelId?.slice(-8) ?? "null"} show=${showMessageLoadingSkeleton} ` +
        `count=${messageList.length}/${PRELOAD_MESSAGES_PER_CHANNEL} ` +
        `status=${channelMessagesStatus.type} completedOnce=${hasCompletedOnceRef.current} ` +
        `empty=${channelKnownEmpty} partial=${isDmChannel}`,
    );
    prevSkeletonRef.current = {
      id: channelId,
      show: showMessageLoadingSkeleton,
    };
  }

  useEffect(() => {
    if (!channelId) return;

    if (baseMessageList.length > 0) {
      reconnectSnapshotRef.current[channelId] = baseMessageList;
      return;
    }

    if (channelMessagesStatus.type === "complete") {
      delete reconnectSnapshotRef.current[channelId];
    }
  }, [baseMessageList, channelId, channelMessagesStatus.type]);

  // messageById: Map<string, MessageListItem> over full messageList
  const messageById = useMemo(() => {
    const map = new Map<string, MessageListItem>();
    for (const msg of messageList) {
      map.set(msg.id, msg);
    }
    return map;
  }, [messageList]);

  // Always-on send-trace: every render checks whether any recently-sent
  // optimistic id is now present in the rendered slice. Logs `LANDED`
  // (with elapsed time) the first time a sent id appears, and a `BUG?`
  // warning at 5s if it never does. Diagnostic for the recurring
  // "send-but-don't-render" symptom — see `apps/desktop/src/lib/send-trace.ts`.
  markVisible(channelId, messageById);

  const hasMoreMessages = channelId
    ? channelMessages.length >= messageLimit
    : false;

  // ─── channelMessagesStatus for MessageListView ─────────────────────
  const channelMessagesStatusForList = useMemo(() => {
    if (channelMessagesStatus.type === "error") {
      return {
        type: "error" as const,
        retry:
          typeof channelMessagesStatus.retry === "function"
            ? channelMessagesStatus.retry
            : undefined,
      };
    }
    return { type: channelMessagesStatus.type as "unknown" | "complete" };
    // Depend on .type only — the full object changes on every Zero delivery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMessagesStatus.type]);

  return {
    channelMessages,
    messageList,
    messageById,
    threadSourceIds,
    latestMessageCreatedAt,
    isUsingReconnectSnapshot,
    showMessageLoadingSkeleton,
    channelMessagesStatusForList,
    hasMoreMessages,
  };
}
